import moment from 'moment'
import { CognitoAuth } from 'amazon-cognito-auth-js'
import { cleanup, debugMessage } from './common'
import { urlHasCognitoAuthResponse } from './lib/cognitoCallbackUrl'

const cognito = new CognitoAuth({
  ClientId: '2ps5fmtfnqn08ctjjqekg2ro70',
  AppWebDomain: 'pvpool.auth.us-east-1.amazoncognito.com',
  TokenScopesArray: ['email', 'aws.cognito.signin.user.admin', 'phone', 'openid', 'profile', 'email'],
  RedirectUriSignIn: `${window.location.origin}/login`,
  RedirectUriSignOut: `${window.location.origin}/logout`,
  UserPoolId: 'us-east-1_fUmMKJWwn',
  AdvancedSecurityDataCollectionFlag: 'true'
})

/**
 * amazon-cognito-auth-js is callback-based. parseCognitoWebResponse / getSession
 * may finish synchronously (cached tokens, implicit-flow hash) or later
 * (code exchange, refresh). Callers must subscribe before invoking the SDK.
 *
 * The previous fixed 1s sleeps (added May 2024 while debugging sign-out races)
 * waited even when nothing async was happening. Waiting on onSuccess/onFailure
 * covers the real async cases without stalling a normal page load.
 */
let cognitoWaiter = null

function waitForCognitoUserHandler() {
  if (!cognitoWaiter) {
    let resolve
    let reject
    const promise = new Promise((res, rej) => {
      resolve = res
      reject = rej
    })
    cognitoWaiter = { promise, resolve, reject }
  }
  return cognitoWaiter.promise
}

function settleCognitoWaiter(error, result) {
  if (!cognitoWaiter) return
  const { resolve, reject } = cognitoWaiter
  cognitoWaiter = null
  if (error) reject(error)
  else resolve(result)
}

cognito.userhandler = {
  onSuccess: (result) => {
    debugMessage('cognito.onSuccess', 'authenticator.js')
    settleCognitoWaiter(null, result)
  },
  onFailure: (err) => {
    debugMessage('cognito.onFailure', 'authenticator.js')
    settleCognitoWaiter(err || new Error('Cognito auth failed'))
  }
}

const authenticator = {
  async signIn() {
    // getSession() only fires onSuccess when the SDK actually does work.
    // A second signIn() after a valid session would wait forever on a new
    // waiter, which leaves RouterProvider on the splash with no network and
    // no console errors.
    try {
      if ((await this.isTokenValid()) && cognito.getCurrentUser()) {
        debugMessage('signIn() skipped, session already valid', 'authenticator.js')
        return true
      }
    } catch (e) {}

    const settled = waitForCognitoUserHandler()
    cognito.getSession()
    await settled
    return true
  },

  async signOut() {
    cognito.signOut()
    // SDK clears tokens then launchUri() to the Cognito logout URL. Hold the
    // caller so React Router cannot continue (e.g. bounce to /home) before the
    // page unloads. Replaces the previous 1s sleep added for that race.
    await new Promise(() => {})
  },

  async isAuthenticated() {
    if (urlHasCognitoAuthResponse(window.location.href)) {
      const settled = waitForCognitoUserHandler()
      cognito.parseCognitoWebResponse(window.location.href)
      try {
        await settled
      } catch (e) {
        return false
      }
    }

    const authed = (await this.isTokenValid()) && cognito.getCurrentUser()
    return authed
  },

  async isTokenValid() {
    const idToken = this.getToken()
    const tokenExpiry = idToken?.decoded?.exp
    if (!tokenExpiry) return false

    const currentTime = moment().format('X')
    return parseInt(currentTime, 10) < parseInt(tokenExpiry, 10)
  },

  getToken(recoverOnProblem = false) {
    const parseJwt = (token) => {
      if (!token) return null
      var base64Url = token.split('.')[1]
      var base64 = base64Url.replace('-', '+').replace('_', '/')
      return JSON.parse(window.atob(base64))
    }

    // check that a cognito token is in localstorage:
    let itemFound = false
    for (let i in window.localStorage) if (i.includes('CognitoIdentityServiceProvider')) itemFound = true
    if (!itemFound) {
      if (recoverOnProblem) cleanup({ initiator: 'authenticator' })
      return { jwt: null }
    }

    try {
      const jwtToken = cognito.getCachedSession().getIdToken().jwtToken
      const token = {
        jwt: jwtToken,
        decoded: parseJwt(jwtToken)
      }

      if (!token || !token.decoded || !token.decoded.exp) {
        if (recoverOnProblem) cleanup({ initiator: 'authenticator' })
        return { jwt: null }
      }

      return token
    } catch (error) {
      if (recoverOnProblem) cleanup({ initiator: 'authenticator' })
      return { jwt: null }
    }
  },

  getUserDetails() {
    const { decoded } = this.getToken() || {}
    const { name, email } = decoded || {}
    return { name, email }
  }
}

export { authenticator }
